/*
* Copyright 2026 Tecnalia, Basque Research & Technology Alliance (BRTA)
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*  SPDX-License-Identifier: Apache-2.0
*/

import { EdcCollapsibleJsonViewComponent } from '../../shared/edc-collapsible-json-view/edc-collapsible-json-view.component';
import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { EdcInfoModalTabsComponent } from '../../shared/edc-info-modal-tabs/edc-info-modal-tabs.component';
import { GetCatalogService } from 'app/core/services/catalog/get-catalog.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from 'app/auth/services/auth.service';
import { Offer } from 'app/core/models/catalog-dto';
import { Asset } from '../../core/models/asset';
import { CommonModule } from '@angular/common';
import {
    summarizeOffersHuman,
    summarizePolicyConstraints,
    type OfferHumanSummary,
    type SummarizePolicyOptions
} from '../../core/utils/offer-policy-summary';
import { Modal, Toast } from 'bootstrap';
import { Router } from '@angular/router';

type AssetWithOffers = Asset & { offers: Offer[] };
type FilterKey = 'connector' | 'contentType';
const FILTER_KEYS: FilterKey[] = ['connector', 'contentType'];

const PROPERTY_LABEL_I18N_KEYS: Record<string, string> = {
    contenttype: 'CATALOG.ASSET.PROPERTY-LABEL.CONTENT-TYPE',
    id: 'CATALOG.ASSET.PROPERTY-LABEL.ID',
    name: 'CATALOG.ASSET.PROPERTY-LABEL.NAME',
    version: 'CATALOG.ASSET.PROPERTY-LABEL.VERSION'
};

@Component({
    selector: 'app-catalog',
    standalone: true,
    imports: [
        EdcCollapsibleJsonViewComponent,
        EdcInfoModalTabsComponent,
        TranslateModule,
        CommonModule
    ],
    templateUrl: './catalog.component.html',
    styleUrls: ['./catalog.component.scss']
})

export class CatalogComponent implements AfterViewInit, OnInit, OnDestroy {

    @ViewChild('catalogSearcher') catalogSearcher!: ElementRef<HTMLInputElement>;

    @ViewChild('infoAssetModal') infoAssetModalRef!: ElementRef;
    public infoAssetModal!: Modal;

    @ViewChild('propertiesAssetModal') propertiesAssetModalRef!: ElementRef;
    public propertiesAssetModal!: Modal;

    @ViewChild('offersAssetModal') offersAssetModalRef!: ElementRef;
    public offersAssetModal!: Modal;

    @ViewChild('toastElement', { static: true }) toastEl!: ElementRef;
    toastMessage: string = '';
    copiedOriginatorAssetKey: string | null = null;
    private copiedOriginatorFeedbackTimer: ReturnType<typeof setTimeout> | null = null;
    copiedOfferIndex: number | null = null;
    private copiedOfferFeedbackTimer: ReturnType<typeof setTimeout> | null = null;

    isLoading = true;
    showFilters = false;
    openDropdown: FilterKey | null = null;

    assets: AssetWithOffers[] = [];
    filteredAssets: AssetWithOffers[] = [];

    sortCriteria = '';
    sortDirection: 'asc' | 'desc' = 'asc';

    filters: Record<FilterKey, Set<string>> = {
        connector: new Set<string>(),
        contentType: new Set<string>()
    };

    filterOptionSearch: Record<FilterKey, string> = {
        connector: '',
        contentType: ''
    };

    selectedAssetJSON: string = '';
    selectedAsset: AssetWithOffers | null = null;
    selectedAssetExtraPublicRows: { key: string; value: string }[] = [];
    selectedAssetPrivateRows: { key: string; value: string }[] = [];

    selectedPropertyRows: { key: string; value: string; labelKey?: string }[] = [];

    selectedOffersSummaries: OfferHumanSummary[] = [];
    private filterValueCache: Record<FilterKey, Map<AssetWithOffers, string>> = {
        connector: new Map<AssetWithOffers, string>(),
        contentType: new Map<AssetWithOffers, string>()
    };
    private searchHaystackCache = new Map<AssetWithOffers, string>();
    private originatorCopyKeyCache = new WeakMap<AssetWithOffers, string>();
    private nextOriginatorCopyKey = 0;

    constructor(
        private getCatalogService: GetCatalogService,
        private translate: TranslateService,
        private authService: AuthService,
        private router: Router
    ) { }

    get showCatalogEmpty(): boolean {
        return !this.isLoading && this.assets.length === 0;
    }

    isCatalogAdministrator(): boolean {
        return this.authService.checkRealmRolesIncludeAnyOfRequiredRoles(['ds-catalog-administrator']);
    }

    goToAdministration(): void {
        void this.router.navigate(['/home', 'administration']);
    }

    private policySummaryOptions(extra: SummarizePolicyOptions = {}): SummarizePolicyOptions {
        const t = (key: string, interp?: Record<string, string>) =>
            this.translate.instant(`CATALOG.ASSET.${key}`, interp);
        return {
            locale: this.translate.currentLang || 'es',
            labels: {
                timeRange: (start, end) => t('POLICY-TIME-RANGE', { start, end }),
                timeFrom: (date) => t('POLICY-TIME-FROM', { date }),
                timeUntil: (date) => t('POLICY-TIME-UNTIL', { date }),
                locationIsAnyOf: (values) => t('POLICY-LOCATION-ANY', { values }),
                locationEq: (value) => t('POLICY-LOCATION-EQ', { value }),
                generic: (left, op, right) => t('POLICY-GENERIC', { left, op, right })
            },
            ...extra
        };
    }

    ngOnInit(): void {
        this.getCatalog();
        this.filteredAssets = [...this.assets];
    }

    ngAfterViewInit(): void {
        this.infoAssetModal = new Modal(this.infoAssetModalRef.nativeElement);
        this.propertiesAssetModal = new Modal(this.propertiesAssetModalRef.nativeElement);
        this.offersAssetModal = new Modal(this.offersAssetModalRef.nativeElement);
    }

    ngOnDestroy(): void {
        this.infoAssetModal?.dispose();
        this.propertiesAssetModal?.dispose();
        this.offersAssetModal?.dispose();
        this.clearCopyFeedbackTimers();
    }

    // ---------------- FILTER UI ---------------- //

    toggleFilters(): void {
        this.showFilters = !this.showFilters;
        this.openDropdown = null;
    }

    toggleDropdown(key: FilterKey): void {
        this.openDropdown = this.openDropdown === key ? null : key;
    }

    onFilterOptionSearch(key: FilterKey, event: Event): void {
        event.stopPropagation();
        const input = event.target as HTMLInputElement | null;
        this.filterOptionSearch[key] = input?.value ?? '';
    }

    hasActiveCatalogFilters(): boolean {
        return this.getActiveCatalogFiltersCount() > 0;
    }

    getActiveCatalogFiltersCount(): number {
        return this.filters.connector.size + this.filters.contentType.size;
    }

    getVisibleCatalogRowsCount(): number {
        return this.filteredAssets.length;
    }

    hasCatalogQuery(): boolean {
        return this.hasActiveCatalogFilters() || this.currentCatalogSearchTerm().length > 0;
    }

    clearCatalogFilters(): void {
        for (const key of FILTER_KEYS) {
            this.filters[key].clear();
            this.filterOptionSearch[key] = '';
        }
        this.openDropdown = null;
        this.searchCatalog();
    }

    clearCatalogQuery(): void {
        if (this.catalogSearcher?.nativeElement) {
            this.catalogSearcher.nativeElement.value = '';
        }
        this.clearCatalogFilters();
    }

    onFilterToggle(
        key: FilterKey,
        value: string,
        event: MouseEvent
    ): void {
        event.stopPropagation();
        const set = this.filters[key];
        set.has(value) ? set.delete(value) : set.add(value);
        this.pruneUnavailableFilterValues(key);
        this.searchCatalog();
    }

    // ---------------- FILTER LOGIC ---------------- //

    applyAllFilters(): void {
        this.filteredAssets = this.getAssetsMatchingFilters();
        this.applySorting();
    }

    private getAssetsMatchingFilters(): AssetWithOffers[] {
        return this.assets.filter(asset => {
            return FILTER_KEYS.every(key => {
                const values = this.filters[key];
                if (!values.size) return true;
                return values.has(this.getFilterValue(asset, key));
            });
        });
    }

    private getAssetsMatchingFiltersExcept(excludedKey: FilterKey): AssetWithOffers[] {
        return this.assets.filter(asset => {
            return FILTER_KEYS.every(key => {
                if (key === excludedKey) return true;
                const values = this.filters[key];
                if (!values.size) return true;
                return values.has(this.getFilterValue(asset, key));
            });
        });
    }

    getUniqueValues(field: FilterKey): string[] {
        return Array.from(
            new Set(
                this.assets
                    .map(asset => this.getFilterValue(asset, field))
                    .filter(Boolean)
            )
        );
    }

    getAvailableFilterValues(field: FilterKey): string[] {
        return Array.from(
            new Set(
                this.getAssetsMatchingFiltersExcept(field)
                    .map(asset => this.getFilterValue(asset, field))
                    .filter(Boolean)
            )
        );
    }

    getAvailableFilterValuesCount(field: FilterKey): number {
        return this.getAvailableFilterValues(field).length;
    }

    getVisibleFilterValues(field: FilterKey): string[] {
        const term = this.filterOptionSearch[field].trim().toLowerCase();
        const values = this.getAvailableFilterValues(field);
        if (!term) return values;
        return values.filter(value => value.toLowerCase().includes(term));
    }

    private pruneUnavailableFilterValues(changedKey: FilterKey): void {
        for (const key of FILTER_KEYS) {
            if (key === changedKey) continue;

            const availableValues = new Set(this.getAvailableFilterValues(key));
            for (const value of Array.from(this.filters[key])) {
                if (!availableValues.has(value)) {
                    this.filters[key].delete(value);
                }
            }
        }
    }

    // ---------------- SEARCH ---------------- //

    searchCatalog(): void {
        const term = this.currentCatalogSearchTerm();
        const base = this.getAssetsMatchingFilters();

        if (!term) {
            this.filteredAssets = base;
            this.applySorting();
            return;
        }

        this.filteredAssets = base.filter((asset) => this.assetMatchesCatalogSearch(asset, term));
        this.applySorting();
    }

    resetSearch(): void {
        this.catalogSearcher.nativeElement.value = '';
        this.applyAllFilters();
    }

    private assetMatchesCatalogSearch(asset: AssetWithOffers, term: string): boolean {
        return this.getAssetSearchHaystack(asset).includes(term);
    }

    private currentCatalogSearchTerm(): string {
        return this.catalogSearcher?.nativeElement.value.trim().toLowerCase() ?? '';
    }

    private getFilterValue(asset: AssetWithOffers, field: FilterKey): string {
        const cache = this.filterValueCache[field];
        const cached = cache.get(asset);
        if (cached !== undefined) return cached;

        const value = field === 'connector' ? asset.connector : asset.contentType;
        const normalized = value ?? '';
        cache.set(asset, normalized);
        return normalized;
    }

    private rebuildCatalogIndexes(): void {
        this.filterValueCache = {
            connector: new Map<AssetWithOffers, string>(),
            contentType: new Map<AssetWithOffers, string>()
        };
        this.searchHaystackCache = new Map<AssetWithOffers, string>();

        for (const asset of this.assets) {
            this.getFilterValue(asset, 'connector');
            this.getFilterValue(asset, 'contentType');
            this.searchHaystackCache.set(asset, this.buildAssetSearchHaystack(asset));
        }
    }

    private getAssetSearchHaystack(asset: AssetWithOffers): string {
        const cached = this.searchHaystackCache.get(asset);
        if (cached !== undefined) return cached;

        const haystack = this.buildAssetSearchHaystack(asset);
        this.searchHaystackCache.set(asset, haystack);
        return haystack;
    }

    private buildAssetSearchHaystack(asset: AssetWithOffers): string {
        return [
            asset.connector,
            asset.ID,
            asset.name,
            asset.contentType,
            asset.originator,
            this.propertySearchHaystack(asset),
            this.offerSearchHaystack(asset)
        ].filter(Boolean).join('\u0001').toLowerCase();
    }

    private propertySearchHaystack(asset: AssetWithOffers): string {
        const parts: string[] = [];
        const props = asset.originalDTO.properties ?? {};
        for (const [key, value] of Object.entries(props)) {
            parts.push(key);
            parts.push(this.formatPropertyValue(value));
            const labelKey = PROPERTY_LABEL_I18N_KEYS[key.toLowerCase()];
            if (labelKey) {
                parts.push(this.translate.instant(labelKey));
            }
            parts.push(this.humanizePropertyKey(key));
        }
        return parts.join('\u0001').toLowerCase();
    }

    private offerSearchHaystack(asset: AssetWithOffers): string {
        const opts = this.policySummaryOptions({ maxRightOperandLength: 800, maxLength: 12000 });
        const parts: string[] = [];
        parts.push(summarizePolicyConstraints(asset.offers, opts));
        for (const offer of asset.offers) {
            const id = offer['@id'];
            if (typeof id === 'string' && id.length) {
                parts.push(id);
            }
        }
        try {
            let raw = JSON.stringify(asset.offers);
            if (raw.length > 20000) {
                raw = raw.slice(0, 20000);
            }
            parts.push(raw);
        } catch {
            /* ignore */
        }
        return parts.filter(Boolean).join('\u0001').toLowerCase();
    }

    // ---------------- SORT ---------------- //

    sortAssetsBy(criteria: string): void {
        this.sortDirection =
            this.sortCriteria === criteria && this.sortDirection === 'asc'
                ? 'desc'
                : 'asc';

        this.sortCriteria = criteria;
        this.applySorting();
    }

    applySorting(): void {
        if (!this.sortCriteria) return;

        this.filteredAssets.sort((a, b) => {
            const av = (a as any)[this.sortCriteria] ?? '';
            const bv = (b as any)[this.sortCriteria] ?? '';

            return this.sortDirection === 'asc'
                ? av.localeCompare(bv, undefined, { numeric: true })
                : bv.localeCompare(av, undefined, { numeric: true });
        });
    }

    // ---------------- CLICK OUTSIDE ---------------- //

    @HostListener('document:click', ['$event'])
    closeDropdown(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        if (!target.closest('.dropdown')) {
            this.openDropdown = null;
        }
    }

    // ---------------- CATALOG ---------------- //

    private getCatalog(): void {
        this.isLoading = true;
        this.getCatalogService.obtain$().subscribe({
            next: ({ assets }) => {
                this.assets = assets;
                this.rebuildCatalogIndexes();
                this.filteredAssets = [...this.assets];
                this.isLoading = false;
            },
            error: (err) => {
                console.error('[CatalogComponent] Error:', err.message);
                this.showToast('Error al obtener el catálogo', 'danger');
                this.isLoading = false;
            }
        });
    }

    // ---------------- COPY TEXT ---------------- //

    getOriginatorCopyKey(asset: AssetWithOffers): string {
        let key = this.originatorCopyKeyCache.get(asset);
        if (!key) {
            key = `originator-${++this.nextOriginatorCopyKey}`;
            this.originatorCopyKeyCache.set(asset, key);
        }
        return key;
    }

    copyToClipboard(text: string, originatorAssetKey?: string): void {
        navigator.clipboard.writeText(text).then(() => {
            if (originatorAssetKey) {
                this.showOriginatorCopyFeedback(originatorAssetKey);
            }
            console.log('Copiado');
        }).catch(err => {
            console.error('Error al copiar: ', err);
        });
    }

    private showOriginatorCopyFeedback(assetKey: string): void {
        if (this.copiedOriginatorFeedbackTimer) {
            clearTimeout(this.copiedOriginatorFeedbackTimer);
        }

        this.copiedOriginatorAssetKey = assetKey;
        this.copiedOriginatorFeedbackTimer = setTimeout(() => {
            this.copiedOriginatorAssetKey = null;
            this.copiedOriginatorFeedbackTimer = null;
        }, 1200);
    }

    copyOfferIdToClipboard(offerId: string, offerIndex: number): void {
        navigator.clipboard.writeText(offerId).then(() => {
            this.showOfferCopyFeedback(offerIndex);
            console.log('Copiado');
        }).catch(err => {
            console.error('Error al copiar: ', err);
        });
    }

    private showOfferCopyFeedback(offerIndex: number): void {
        if (this.copiedOfferFeedbackTimer) {
            clearTimeout(this.copiedOfferFeedbackTimer);
        }

        this.copiedOfferIndex = offerIndex;
        this.copiedOfferFeedbackTimer = setTimeout(() => {
            this.copiedOfferIndex = null;
            this.copiedOfferFeedbackTimer = null;
        }, 1200);
    }

    private clearCopyFeedbackTimers(): void {
        if (this.copiedOriginatorFeedbackTimer) {
            clearTimeout(this.copiedOriginatorFeedbackTimer);
            this.copiedOriginatorFeedbackTimer = null;
        }
        if (this.copiedOfferFeedbackTimer) {
            clearTimeout(this.copiedOfferFeedbackTimer);
            this.copiedOfferFeedbackTimer = null;
        }
    }

    // ---------------- ASSET INFO ---------------- //

    openInfoAsset(asset: AssetWithOffers): void {
        this.showInfoAssetModal(asset);
        this.infoAssetModal.show();
    }

    showInfoAssetModal(asset: AssetWithOffers): void {
        this.selectedAsset = asset;
        this.selectedAssetJSON = JSON.stringify(asset.originalDTO, null, 2);
        this.selectedAssetExtraPublicRows = this.assetExtraPublicProperties(asset);
        this.selectedAssetPrivateRows = this.assetPrivateProperties(asset);
    }

    closeInfoAsset(): void {
        this.selectedAsset = null;
        this.selectedAssetJSON = '';
        this.selectedAssetExtraPublicRows = [];
        this.selectedAssetPrivateRows = [];
        this.infoAssetModal.hide();
    }

    private assetExtraPublicProperties(asset: AssetWithOffers): { key: string; value: string }[] {
        const coreKeys = new Set(['contenttype', 'id', 'name', 'version']);
        const props = asset.originalDTO.properties ?? {};
        return Object.keys(props)
            .filter(key => !coreKeys.has(key.toLowerCase()))
            .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
            .map(key => ({
                key,
                value: this.formatPropertyValue(props[key])
            }));
    }

    private assetPrivateProperties(asset: AssetWithOffers): { key: string; value: string }[] {
        const props = asset.originalDTO.privateProperties ?? {};
        return Object.keys(props)
            .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
            .map(key => ({
                key,
                value: this.formatPropertyValue(props[key])
            }));
    }

    // ---------------- ASSET PROPERTIES ---------------- //

    private formatPropertyValue(value: unknown): string {
        if (value === undefined || value === null) {
            return '';
        }
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
        }
        try {
            return JSON.stringify(value);
        } catch {
            return String(value);
        }
    }

    openPropertiesModal(asset: AssetWithOffers): void {
        const props = asset.originalDTO.properties ?? {};
        this.selectedPropertyRows = Object.keys(props)
            .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
            .map((key) => ({
                key,
                value: this.formatPropertyValue(props[key]),
                labelKey: PROPERTY_LABEL_I18N_KEYS[key.toLowerCase()]
            }));
        this.propertiesAssetModal.show();
    }

    closePropertiesModal(): void {
        this.selectedPropertyRows = [];
        this.propertiesAssetModal.hide();
    }

    humanizePropertyKey(key: string): string {
        const spaced = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ');
        return spaced.replace(/\b\w/g, (c) => c.toUpperCase());
    }

    // ---------------- ASSET OFFERS ---------------- //

    openOffersModal(asset: AssetWithOffers): void {
        this.selectedOffersSummaries = summarizeOffersHuman(asset.offers, this.policySummaryOptions());
        this.offersAssetModal.show();
    }

    closeOffersModal(): void {
        this.selectedOffersSummaries = [];
        this.copiedOfferIndex = null;
        if (this.copiedOfferFeedbackTimer) {
            clearTimeout(this.copiedOfferFeedbackTimer);
            this.copiedOfferFeedbackTimer = null;
        }
        this.offersAssetModal.hide();
    }

    // ---------------- TOAST ---------------- //
    
    showToast(message: string, type: 'success' | 'danger' | 'warning' = 'success'): void {
        this.toastMessage = message;
        const el = this.toastEl.nativeElement as HTMLElement;
        el.classList.remove('text-bg-success', 'text-bg-danger', 'text-bg-warning');
        el.classList.add(`text-bg-${type}`);
        const bsToast = new Toast(el);
        bsToast.show();
    }

}